import { useState, type FormEvent } from 'react';
import { Navigate, useLocation } from 'react-router-dom';

import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { ru } from '@/i18n/ru';
import { APP_NAME } from '@setone-sounds/shared/constants';

export function LoginPage() {
  const auth = useAuth();
  const location = useLocation();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);

  if (auth.isLoading) {
    return (
      <div className="flex h-full items-center justify-center text-xs text-text-muted">
        {ru.checkingSession}
      </div>
    );
  }

  if (auth.isAuthenticated) {
    const from = (location.state as { from?: string } | null)?.from ?? '/browse';
    return <Navigate to={from} replace />;
  }

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await auth.login(username, password);
    } catch {
      /* error set in auth context */
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="flex h-full items-center justify-center px-4 animate-fade-in" data-testid="login-page">
      <form
        onSubmit={(e) => void onSubmit(e)}
        className="w-full max-w-sm border border-border bg-surface p-6"
      >
        <div className="mb-6">
          <p className="text-[11px] font-semibold tracking-[0.18em] uppercase text-accent">
            {APP_NAME}
          </p>
          <h1 className="mt-2 text-lg font-medium text-text">{ru.signInTitle}</h1>
          <p className="mt-1 text-xs text-text-muted">{ru.signInHint}</p>
        </div>

        <label className="mb-3 block">
          <span className="mb-1 block text-[11px] uppercase tracking-wider text-text-muted">
            {ru.username}
          </span>
          <Input
            name="username"
            autoComplete="username"
            value={username}
            onChange={(e) => {
              auth.clearLoginError();
              setUsername(e.target.value);
            }}
            required
            data-testid="login-username"
          />
        </label>

        <label className="mb-4 block">
          <span className="mb-1 block text-[11px] uppercase tracking-wider text-text-muted">
            {ru.password}
          </span>
          <Input
            name="password"
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => {
              auth.clearLoginError();
              setPassword(e.target.value);
            }}
            required
            data-testid="login-password"
          />
        </label>

        {auth.loginError ? (
          <p className="mb-3 text-xs text-danger" role="alert" data-testid="login-error">
            {auth.loginError}
          </p>
        ) : null}

        <Button
          type="submit"
          variant="accent"
          className="w-full"
          disabled={submitting || !username || !password}
          data-testid="login-submit"
        >
          {submitting ? ru.signingIn : ru.signIn}
        </Button>
      </form>
    </div>
  );
}
